import { ORDER_STATUSES } from '@/types';
import type { AnalyticsReport, AdminNotification, InventoryRow, PendingAction, RangeKey, SearchHit } from '@/types/admin';
import { can } from '@/lib/permissions';
import { formatPrice } from '@/lib/format';
import type { AdminAnalyticsService, AdminDashboardService, AdminSessionService } from '../../adminContracts';
import { anchorTime, ctx, DAY_MS, delay, hydrateCustomers, isRevenue, matches, stockStatusOf, type TenantData } from './context';

const n = (r: RangeKey) => (r === '7d' ? 7 : r === '30d' ? 30 : 90);
const dayKey = (t: number) => new Date(t).toISOString().slice(0, 10);
const keys = (anchor: number, count: number, offset = 0) => Array.from({ length: count }, (_, i) => dayKey(anchor - (count - 1 - i + offset) * DAY_MS));

/** One aggregation used by BOTH the dashboard and the analytics page. The real API should compute this in SQL. */
export function buildReport(d: TenantData, range: RangeKey): AnalyticsReport {
  const anchor = anchorTime(d);
  const cur = keys(anchor, n(range));
  const prev = keys(anchor, n(range), n(range));
  const inSet = (s: string[]) => (iso: string) => s.includes(iso.slice(0, 10));
  const inCur = inSet(cur);
  const inPrev = inSet(prev);
  const live = d.orders.filter((o) => o.status !== 'CANCELLED');

  const first = new Map<string, string>();
  [...live].sort((a, b) => a.placedAt.localeCompare(b.placedAt)).forEach((o) => { if (!first.has(o.customer.id)) first.set(o.customer.id, o.id); });

  const agg = (pred: (iso: string) => boolean, visitKeys: string[]) => {
    const os = live.filter((o) => pred(o.placedAt));
    const paid = os.filter((o) => isRevenue(o.status));
    const revenue = paid.reduce((s, o) => s + o.pricing.total, 0);
    const visits = visitKeys.reduce((s, k) => s + (d.traffic.find((t) => t.date === k)?.visits ?? 90), 0);
    return { revenue, orders: os.length, customers: new Set(os.map((o) => o.customer.id)).size, aov: paid.length ? revenue / paid.length : 0, conversion: visits ? (os.length / visits) * 100 : 0, visits };
  };
  const a = agg(inCur, cur);
  const b = agg(inPrev, prev);

  const series = cur.map((k) => {
    const os = live.filter((o) => o.placedAt.startsWith(k));
    return {
      date: k, revenue: os.filter((o) => isRevenue(o.status)).reduce((s, o) => s + o.pricing.total, 0), orders: os.length,
      newCustomers: os.filter((o) => first.get(o.customer.id) === o.id).length, returningCustomers: os.filter((o) => first.get(o.customer.id) !== o.id).length,
    };
  });

  const inWindow = d.orders.filter((o) => inCur(o.placedAt));
  const paidItems = inWindow.filter((o) => isRevenue(o.status)).flatMap((o) => o.items);
  const catName = (pid: string) => d.categories.find((c) => c.id === d.products.find((p) => p.id === pid)?.categoryId)?.name ?? 'Other';
  const cats = new Map<string, { revenue: number; units: number }>();
  const prods = new Map<string, { name: string; units: number; revenue: number; icon: (typeof d.products)[number]['icon']; hue: number }>();
  paidItems.forEach((it) => {
    const c = cats.get(catName(it.productId)) ?? { revenue: 0, units: 0 };
    c.revenue += it.price * it.quantity; c.units += it.quantity; cats.set(catName(it.productId), c);
    const p = prods.get(it.productId) ?? { name: it.name, units: 0, revenue: 0, icon: it.icon, hue: it.hue };
    p.units += it.quantity; p.revenue += it.price * it.quantity; prods.set(it.productId, p);
  });
  const methods = new Map<string, { count: number; revenue: number }>();
  inWindow.filter((o) => o.status !== 'CANCELLED').forEach((o) => {
    const m = methods.get(o.paymentMethod) ?? { count: 0, revenue: 0 };
    m.count++; if (isRevenue(o.status)) m.revenue += o.pricing.total; methods.set(o.paymentMethod, m);
  });
  const trafficIn = d.traffic.filter((t) => cur.includes(t.date));

  const report: AnalyticsReport = {
    range,
    kpis: {
      revenue: { value: a.revenue, previous: b.revenue }, orders: { value: a.orders, previous: b.orders }, customers: { value: a.customers, previous: b.customers },
      aov: { value: a.aov, previous: b.aov }, conversion: { value: a.conversion, previous: b.conversion },
    },
    series,
    categories: [...cats].map(([name, v]) => ({ name, ...v })).sort((x, y) => y.revenue - x.revenue),
    topProducts: [...prods].map(([productId, v]) => ({ productId, ...v })).sort((x, y) => y.revenue - x.revenue).slice(0, 6),
    statuses: ORDER_STATUSES.map((status) => ({ status, count: inWindow.filter((o) => o.status === status).length })).filter((s) => s.count > 0),
    payments: [...methods].map(([method, v]) => ({ method, ...v })).sort((x, y) => y.count - x.count),
    funnel: { visits: a.visits, carts: trafficIn.reduce((s, t) => s + t.carts, 0), checkouts: trafficIn.reduce((s, t) => s + t.checkouts, 0), orders: a.orders },
  };
  if (d.features.wallet) {
    const w = d.wallet.filter((x) => inCur(x.createdAt));
    report.walletActivity = { credits: w.filter((x) => x.direction === 'CREDIT').reduce((s, x) => s + x.amount, 0), debits: w.filter((x) => x.direction === 'DEBIT').reduce((s, x) => s + x.amount, 0) };
  }
  if (d.features.loyalty) {
    const l = d.loyalty.filter((x) => inCur(x.createdAt));
    report.loyaltyActivity = { earned: l.filter((x) => x.points > 0).reduce((s, x) => s + x.points, 0), redeemed: -l.filter((x) => x.type === 'REDEEM').reduce((s, x) => s + x.points, 0) };
  }
  return report;
}

const lowStockRows = (d: TenantData): InventoryRow[] =>
  d.products.filter((p) => p.status === 'ACTIVE' && stockStatusOf(p.stock, p.lowStockThreshold) !== 'IN_STOCK')
    .sort((x, y) => x.stock - y.stock)
    .map((p) => ({
      productId: p.id, name: p.name, sku: p.sku, categoryName: d.categories.find((c) => c.id === p.categoryId)?.name ?? '—', stock: p.stock, lowStockThreshold: p.lowStockThreshold,
      status: stockStatusOf(p.stock, p.lowStockThreshold), icon: p.icon, hue: p.hue, image: p.images[0],
    }));

export const adminSession: AdminSessionService = { async me() { return delay(ctx().actor, 250); } };

export const adminAnalytics: AdminAnalyticsService = {
  async report(range) { const { data: d } = ctx('analytics.view'); return delay(buildReport(d, range), 450); },
};

export const adminDashboard: AdminDashboardService = {
  async summary(range) {
    const { actor, data: d } = ctx('dashboard.view');
    const role = actor.user.role;
    const low = lowStockRows(d);
    const pending: PendingAction[] = [];
    const count = (...s: string[]) => d.orders.filter((o) => s.includes(o.status)).length;
    if (d.features.orders && count('PENDING')) pending.push({ id: 'p1', kind: 'ORDERS_PENDING', label: 'Orders awaiting payment', count: count('PENDING'), to: '/admin/orders?status=PENDING' });
    if (d.features.orders && count('PAID', 'PROCESSING')) pending.push({ id: 'p2', kind: 'ORDERS_PENDING', label: 'Orders ready to ship', count: count('PAID', 'PROCESSING'), to: '/admin/orders?status=PAID' });
    if (low.length) pending.push({ id: 'p3', kind: 'LOW_STOCK', label: 'Products low or out of stock', count: low.length, to: '/admin/inventory?status=LOW' });
    if (d.features.affiliate && can(role, 'referrals.manage')) {
      const c = d.commissions.filter((x) => x.status === 'PENDING' || x.status === 'APPROVED').length;
      if (c) pending.push({ id: 'p4', kind: 'COMMISSIONS', label: 'Commissions to review or pay', count: c, to: '/admin/referrals' });
    }
    if (can(role, 'marketing.manage')) {
      const c = d.campaigns.filter((x) => x.status === 'DRAFT').length;
      if (c) pending.push({ id: 'p5', kind: 'CAMPAIGN_DRAFT', label: 'Draft campaigns', count: c, to: '/admin/campaigns' });
    }
    return delay({
      report: buildReport(d, range),
      recentOrders: [...d.orders].sort((a, b) => b.placedAt.localeCompare(a.placedAt)).slice(0, 6),
      lowStock: low.slice(0, 6),
      recentCustomers: hydrateCustomers(d).sort((a, b) => b.joinedAt.localeCompare(a.joinedAt)).slice(0, 5),
      pending,
    }, 500);
  },
  async search(q) {
    const { actor, data: d } = ctx('dashboard.view');
    const role = actor.user.role;
    const hits: SearchHit[] = [];
    if (q.trim().length < 2) return delay(hits, 100);
    if (can(role, 'catalog.read') && d.features.products) {
      d.products.filter((p) => matches(q, p.name, p.sku, p.brand)).slice(0, 4).forEach((p) => hits.push({ kind: 'product', id: p.id, title: p.name, subtitle: `SKU ${p.sku} · ${formatPrice(p.price)}`, to: `/admin/products/${p.id}` }));
    }
    if (can(role, 'orders.read') && d.features.orders) {
      d.orders.filter((o) => matches(q, o.number, o.customer.name)).slice(0, 4).forEach((o) => hits.push({ kind: 'order', id: o.id, title: `Order ${o.number}`, subtitle: `${o.customer.name} · ${formatPrice(o.pricing.total)}`, to: `/admin/orders/${o.id}` }));
    }
    if (can(role, 'customers.read')) {
      d.customers.filter((c) => matches(q, c.name, c.email)).slice(0, 4).forEach((c) => hits.push({ kind: 'customer', id: c.id, title: c.name, subtitle: c.email, to: `/admin/customers/${c.id}` }));
    }
    return delay(hits, 200);
  },
  async notifications() {
    const { actor, data: d } = ctx('dashboard.view');
    const out: AdminNotification[] = [];
    const now = new Date(anchorTime(d)).toISOString();
    const pend = d.orders.filter((o) => o.status === 'PENDING').length;
    if (d.features.orders && pend) out.push({ id: `n_pend_${pend}`, title: `${pend} orders awaiting payment`, body: 'Follow up or cancel stale orders.', at: now, to: '/admin/orders?status=PENDING', tone: 'warning' });
    const low = lowStockRows(d);
    if (low.length) out.push({ id: `n_low_${low.length}`, title: `${low.length} products need restocking`, body: low.slice(0, 2).map((p) => p.name).join(', '), at: now, to: '/admin/inventory?status=LOW', tone: 'warning' });
    if (d.features.affiliate && can(actor.user.role, 'referrals.manage')) {
      const c = d.commissions.filter((x) => x.status === 'PENDING').length;
      if (c) out.push({ id: `n_com_${c}`, title: `${c} commissions await approval`, body: 'Review referral commissions.', at: now, to: '/admin/referrals', tone: 'info' });
    }
    const newest = [...d.customers].sort((a, b) => b.joinedAt.localeCompare(a.joinedAt))[0];
    if (newest) out.push({ id: `n_cus_${newest.id}`, title: 'New customer joined', body: newest.name, at: newest.joinedAt, to: `/admin/customers/${newest.id}`, tone: 'success' });
    return delay(out, 200);
  },
};
